// Aadhaar offline e-KYC. Reads the user-downloaded share-code zip from
// https://myaadhaar.uidai.gov.in, parses the embedded XML, and verifies the
// UIDAI RSA-2048 signature. The Aadhaar number itself is never present in
// the offline payload — only a reference id, last four digits, and a
// signed demographic block.
//
// This connector is 100% local. The zip is decrypted with the user's
// share code (4-digit, user-chosen at download time). We never POST the
// payload anywhere.
//
// Production note: the integrating app must ship the UIDAI public-key
// certificate (PEM) at /public/aadhaar-uidai-cert.pem. The connector
// pulls it via fetch('/aadhaar-uidai-cert.pem'). In test mode we skip
// signature verification and emit a labelled mock.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, liveDisabled, nowIso, ok, shortHash } from './_types.ts';

export interface AadhaarOfflineKyc {
  readonly referenceId: string;
  readonly aadhaarLast4: string;
  readonly name: string;
  readonly dob: string;
  readonly gender: 'M' | 'F' | 'T';
  readonly addressLine: string;
  readonly state: string;
  readonly pincode: string;
  readonly photoB64: string;
  readonly signatureValid: boolean;
  readonly issuedAt: string;
}

export interface AadhaarConnectorStatus extends ConnectorStatus {
  readonly id: 'aadhaar-offline-kyc';
  readonly publicKeyLoaded: boolean;
}

interface AadhaarPersist {
  readonly connectedAt: string;
  readonly aadhaarLast4: string;
  readonly referenceIdHash: string;
}

const STORE_KEY = 'empire/connectors/aadhaar-offline-kyc';

export class AadhaarOfflineKycAdapter
  implements ConnectorAdapter<AadhaarConnectorStatus, AadhaarOfflineKyc>
{
  readonly id = 'aadhaar-offline-kyc';
  readonly displayName = 'Aadhaar offline e-KYC';
  readonly displayNameHi = 'आधार ऑफ़लाइन ई-केवाईसी';
  readonly dpdpCategory = 'identity-aadhaar-offline';
  readonly mode: ConnectorMode;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<AadhaarConnectorStatus> {
    const persisted = readPersist();
    const publicKeyLoaded = this.mode === 'live' ? await this.#tryLoadPublicKey() : true;
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.referenceIdHash ?? null,
      publicKeyLoaded,
      notes: this.mode === 'live'
        ? 'Live signature verification against UIDAI public key.'
        : 'Test mode — RSA verification skipped, mock demographic returned.',
    };
  }

  async connect(): Promise<ConnectorResult<AadhaarConnectorStatus>> {
    // The act of "connecting" is selecting a zip + share code; we surface
    // an importing modal in the integrating app. Here we just confirm
    // readiness and ensure the public-key cert is reachable in live mode.
    if (this.mode === 'live') {
      const loaded = await this.#tryLoadPublicKey();
      if (!loaded) {
        return err({
          code: 'not-configured',
          message: 'UIDAI public-key certificate not bundled. Drop the PEM at /aadhaar-uidai-cert.pem.',
          retriable: false,
        });
      }
    }
    const status = await this.getStatus();
    return ok(status);
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  async importPayload(): Promise<ConnectorResult<AadhaarOfflineKyc>> {
    // Apps trigger import via importFromZip with the parsed zip bytes.
    return err({ code: 'unsupported', message: 'Use importFromZip(bytes, shareCode) instead.', retriable: false });
  }

  mockPayload(): AadhaarOfflineKyc {
    return {
      referenceId: '2026XXXX',
      aadhaarLast4: '4321',
      name: 'Aditya Sharma',
      dob: '1992-04-15',
      gender: 'M',
      addressLine: 'House No. 14, New Friends Colony, South Delhi',
      state: 'Delhi',
      pincode: '110025',
      photoB64: '',
      signatureValid: true,
      issuedAt: nowIso(),
    };
  }

  async importFromZip(zipBytes: Uint8Array, shareCode: string): Promise<ConnectorResult<AadhaarOfflineKyc>> {
    if (this.mode !== 'live') {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    try {
      const xml = await openShareCodeZip(zipBytes, shareCode);
      const parsed = parseAadhaarXml(xml);
      const certPem = await this.#loadPublicKeyPem();
      if (!certPem) {
        return err({ code: 'not-configured', message: 'UIDAI public-key PEM not available.', retriable: false });
      }
      const valid = await verifyRsa2048Signature(xml, parsed.signatureB64, certPem);
      if (!valid) {
        return err({ code: 'signature', message: 'UIDAI signature did not verify against bundled cert.', retriable: false });
      }
      const payload: AadhaarOfflineKyc = {
        referenceId: parsed.referenceId,
        aadhaarLast4: parsed.aadhaarLast4,
        name: parsed.name,
        dob: parsed.dob,
        gender: parsed.gender,
        addressLine: parsed.addressLine,
        state: parsed.state,
        pincode: parsed.pincode,
        photoB64: parsed.photoB64,
        signatureValid: true,
        issuedAt: nowIso(),
      };
      await this.#persist(payload);
      return ok(payload);
    } catch (e) {
      return err({
        code: 'parse',
        message: e instanceof Error ? e.message : 'Aadhaar zip parse failed.',
        retriable: false,
      });
    }
  }

  async #persist(p: AadhaarOfflineKyc): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const record: AadhaarPersist = {
      connectedAt: nowIso(),
      aadhaarLast4: p.aadhaarLast4,
      referenceIdHash: await shortHash(p.referenceId),
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(record));
  }

  async #tryLoadPublicKey(): Promise<boolean> {
    return (await this.#loadPublicKeyPem()) !== null;
  }

  async #loadPublicKeyPem(): Promise<string | null> {
    try {
      const res = await fetch('/aadhaar-uidai-cert.pem');
      if (!res.ok) return null;
      const text = await res.text();
      return text.includes('BEGIN CERTIFICATE') ? text : null;
    } catch {
      return null;
    }
  }
}

function readPersist(): AadhaarPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AadhaarPersist;
  } catch {
    return null;
  }
}

// ────────────────── parsing helpers ──────────────────
// We avoid bundling a zip library. The Aadhaar offline payload is a single
// XML file inside an AES-128 encrypted zip. In live mode the app routes the
// decryption through a small Web Worker built on top of the browser-native
// DecompressionStream + AES-128-CBC via subtle.crypto. For v0 the zip path
// is left as a TODO and live mode will surface a clear error.

async function openShareCodeZip(_bytes: Uint8Array, _shareCode: string): Promise<string> {
  throw new Error('share-code-zip-decryption-not-yet-bundled');
}

interface ParsedAadhaar {
  readonly referenceId: string;
  readonly aadhaarLast4: string;
  readonly name: string;
  readonly dob: string;
  readonly gender: 'M' | 'F' | 'T';
  readonly addressLine: string;
  readonly state: string;
  readonly pincode: string;
  readonly photoB64: string;
  readonly signatureB64: string;
}

function parseAadhaarXml(xml: string): ParsedAadhaar {
  // The offline e-KYC XML schema uses attributes on Poi / Poa / Pht / SignatureValue.
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const root = doc.documentElement;
  const poi = root.getElementsByTagName('Poi')[0];
  const poa = root.getElementsByTagName('Poa')[0];
  const pht = root.getElementsByTagName('Pht')[0];
  const sigEl = root.getElementsByTagName('SignatureValue')[0];
  const referenceId = root.getAttribute('referenceId') ?? '';
  const aadhaarLast4 = referenceId.slice(0, 4) || '0000';
  const name = poi?.getAttribute('name') ?? '';
  const dob = poi?.getAttribute('dob') ?? '';
  const gender = (poi?.getAttribute('gender') ?? 'M') as 'M' | 'F' | 'T';
  const addressLine = [
    poa?.getAttribute('house'),
    poa?.getAttribute('street'),
    poa?.getAttribute('lm'),
    poa?.getAttribute('loc'),
    poa?.getAttribute('vtc'),
    poa?.getAttribute('dist'),
  ].filter(Boolean).join(', ');
  const state = poa?.getAttribute('state') ?? '';
  const pincode = poa?.getAttribute('pc') ?? '';
  return {
    referenceId,
    aadhaarLast4,
    name,
    dob,
    gender,
    addressLine,
    state,
    pincode,
    photoB64: pht?.textContent?.trim() ?? '',
    signatureB64: sigEl?.textContent?.trim() ?? '',
  };
}

async function verifyRsa2048Signature(
  xml: string,
  signatureB64: string,
  certPem: string
): Promise<boolean> {
  try {
    const sig = Uint8Array.from(atob(signatureB64), (c) => c.charCodeAt(0));
    const spki = pemToDer(certPem);
    const key = await crypto.subtle.importKey(
      'spki',
      spki as unknown as BufferSource,
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['verify']
    );
    const data = new TextEncoder().encode(stripSignature(xml));
    return crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      key,
      sig as unknown as BufferSource,
      data as unknown as BufferSource
    );
  } catch {
    return false;
  }
}

function pemToDer(pem: string): Uint8Array {
  const b64 = pem
    .replace(/-----BEGIN[^-]+-----/, '')
    .replace(/-----END[^-]+-----/, '')
    .replace(/\s+/g, '');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function stripSignature(xml: string): string {
  return xml.replace(/<SignatureValue[^>]*>[^<]*<\/SignatureValue>/g, '<SignatureValue></SignatureValue>');
}

export function maskAadhaar(last4: string): string {
  return `XXXX XXXX ${last4}`;
}

// Surface to allow integrators to flip live mode without rebuilding.
export function makeAadhaarConnector(mode: ConnectorMode): AadhaarOfflineKycAdapter {
  return new AadhaarOfflineKycAdapter(mode);
}

// Stamp the build so we can wire the live mode after the share-code-zip
// worker lands.
export const AADHAAR_BUILD_NOTES = Object.freeze({
  schemaVersion: 1,
  cert: '/aadhaar-uidai-cert.pem',
  zipDecryption: 'AES-128-CBC, share-code keyed, decoder pending v1.1',
  liveBlocked: ['openShareCodeZip'],
});

// Tells the runtime whether live is wired end-to-end.
export function aadhaarLiveReady(): boolean {
  return false;
}

// Keep liveDisabled discoverable for app-level error mapping.
export { liveDisabled };
